import type { ItemType, Product } from "./api";

/**
 * What kind of thing a product is, by its full name, and where it sits on the shelf (D,
 * 12 October 2026: "Booster Box Case", "Booster Pack", not "Box" and "Pack"). The type
 * says what the product is; the subcategory is the group it is shown in.
 */
export const PRODUCT_TYPES: readonly { readonly value: ItemType; readonly label: string; readonly unit: string; readonly section: string }[] = [
  { value: "pack", label: "Booster Pack", unit: "pack", section: "Booster Packs" },
  { value: "box", label: "Booster Box", unit: "box", section: "Booster Boxes" },
  { value: "bundle", label: "Booster Bundle", unit: "bundle", section: "Booster Bundles" },
  { value: "etb", label: "Elite Trainer Box", unit: "box", section: "Elite Trainer Boxes" },
  { value: "case", label: "Case (of boxes or bundles)", unit: "case", section: "Cases & Displays" },
  { value: "display", label: "Inner Display", unit: "display", section: "Cases & Displays" },
  { value: "collection", label: "Collection or Gift Box", unit: "box", section: "Collections & Sets" },
  { value: "tin", label: "Tin", unit: "tin", section: "Collections & Sets" },
  { value: "single", label: "Single Card", unit: "card", section: "" },
  { value: "accessory", label: "Accessory", unit: "item", section: "Others" },
  { value: "other", label: "Other Product", unit: "item", section: "Others" },
];

export function typeOf(itemType: ItemType): (typeof PRODUCT_TYPES)[number] {
  return PRODUCT_TYPES.find((one) => one.value === itemType) ?? PRODUCT_TYPES[PRODUCT_TYPES.length - 1]!;
}

/** Something that holds other sealed things, and so can be opened into them. */
export function holdsThings(itemType: ItemType): boolean {
  return ["box", "bundle", "etb", "case", "display", "collection", "tin"].includes(itemType);
}

const plural = (count: number, word: string): string => (count === 1 ? word : word.endsWith("x") ? `${word}es` : `${word}s`);

/**
 * How many packs are in one of these, worked down through what it holds: a case of 6 boxes
 * of 24 packs is 144. Null when the chain does not end in a known number of packs.
 */
export function packsInside(
  holds: { readonly containsProductId: string | null; readonly containsQuantity: number | null; readonly packsPerBox: number | null },
  products: readonly Product[],
): number | null {
  let count = 1;
  let at: { containsProductId: string | null; containsQuantity: number | null; packsPerBox: number | null; itemType?: ItemType } | undefined = holds;
  for (let depth = 0; at && depth < 8; depth++) {
    if (!at.containsProductId || !at.containsQuantity) return at.packsPerBox ? count * at.packsPerBox : depth > 0 && at.itemType === "pack" ? count : null;
    count *= at.containsQuantity;
    const next = products.find((one) => one.id === at?.containsProductId);
    at = next
      ? { containsProductId: next.containsProductId ?? null, containsQuantity: next.containsQuantity ?? null, packsPerBox: next.packsPerBox, itemType: next.itemType }
      : undefined;
  }
  return null;
}

/** "Quantity 1 is one sealed case holding 6 EN Origins Booster Box, 144 booster packs in all." */
export function contentsSentence(
  itemType: ItemType,
  holds: { readonly containsProductId: string | null; readonly containsQuantity: number | null; readonly packsPerBox: number | null },
  products: readonly Product[],
): string {
  const unit = typeOf(itemType).unit;
  const inside = products.find((one) => one.id === holds.containsProductId);
  const packs = packsInside(holds, products);
  const start = `Quantity 1 is one ${itemType === "single" ? "card" : `sealed ${unit}`}`;
  if (inside && holds.containsQuantity) {
    const tail = packs && inside.itemType !== "pack" ? `, ${packs} booster ${plural(packs, "pack")} in all` : "";
    return `${start} holding ${holds.containsQuantity} x ${inside.name}${tail}.`;
  }
  if (packs) return `${start} of ${packs} booster ${plural(packs, "pack")}.`;
  return `${start}.`;
}
